import { useLocalStorage } from '@mantine/hooks';
import {
  GridApiPro,
  GridColDef,
  GridRowsProp,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { getBaseCleanUrl } from 'components/ImageUpload/ImageData';
import { createContext, memo, useCallback, useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { UseLocationType } from 'utility/types/cosmosTypes';
import { HandleUseAssetSelectFn } from '../components/common/FileFieldInput/hooks/useFileFieldAssetSelection';
import {
  PreviewStateDataType,
  PreviewUpdateState,
  TriggerAssetDrop,
} from '../components/common/FileFieldInput/types';
import { RelationshipType } from '../components/common/RelatedAssetsDrawer/utils/relationshipUtils';
import { useAssetConfigContext } from './AssetConfigContextProvider';
import { useAssetEditorsContext } from './AssetEditorsContextProvider';
import { TDynamicModalState } from '../components/collection/components/CollectionDynamicModal';
import { TPromptModalState } from '../components/collection/types';

// -----------------------------------------------------------------
// Types
// -----------------------------------------------------------------

// Type for field visibility setter functions
// Supports both direct boolean values and updater functions
type FieldVisibilitySetter = boolean | ((prev: boolean) => boolean);

// Widget ids and correlating titles for
// the asset's stats dashboard for navigation.
type TStatsMenuList = {
  id: string;
  title: string;
};

export type AssetViewProviderProps = {
  children?: React.ReactNode;
};

// Table asset specific states...
export type TableRowsCols = {
  rows: GridRowsProp | null;
  columns: GridColDef[] | null;
};

export type TableCtxState = {
  rows: TableRowsCols['rows'];
  columns: TableRowsCols['columns'];
  loading: boolean;
  loadingText: string;
};

export type RightDrawerStates =
  | 'assistant'
  | 'relationships'
  | 'comments'
  | 'publications'
  | 'notifier';

export type LeftDrawerStates = 'content' | 'metadata' | 'stats' | 'revisions';

// Cover art type / initial state - data synced in CoverArtUpload.tsx
export type TEditCoverArtMode = 'upload' | 'options' | 'display' | null;
export type TCoverArtState = Partial<PreviewStateDataType>;
export const initCoverArtData: TCoverArtState = {
  fileSourceUrl: undefined,
  basePreviewUrl: undefined,
  cropPreviewUrl: undefined,
  width: undefined,
  height: undefined,
  view_crop: undefined,
};

// Save button split menu state
// Highlight optional for css on open
type SplitSaveMenuState = {
  open: boolean;
  highlight: boolean;
};

export const initSplitSaveMenuState: SplitSaveMenuState = {
  open: false,
  highlight: false,
};

export const initDcPromptModalState: TPromptModalState = {
  open: false,
  targetType: null,
};

export const initDcModalState: TDynamicModalState = {
  open: false,
  assetFormValues: null,
};

export type AssetViewContextType = {
  leftDrawerOpen: boolean;
  leftDrawerContent: LeftDrawerStates;
  setLeftDrawerContent: React.Dispatch<React.SetStateAction<LeftDrawerStates>>;
  setLeftDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  toggleLeftDrawer: () => void;
  setRightDrawerContent: React.Dispatch<React.SetStateAction<RightDrawerStates>>;
  assistantContent: string;
  setAssistantContent: React.Dispatch<React.SetStateAction<string>>;
  rightDrawerOpen: boolean;
  rightDrawerContent: RightDrawerStates;
  setRightDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  previewUploadAsset: TriggerAssetDrop | null;
  setPreviewUploadAsset: React.Dispatch<React.SetStateAction<TriggerAssetDrop | null>>;
  coverUploadAsset: TriggerAssetDrop | null;
  setCoverUploadAsset: React.Dispatch<React.SetStateAction<TriggerAssetDrop | null>>;
  // cover art
  coverArtData: TCoverArtState;
  setCoverArtData: React.Dispatch<React.SetStateAction<TCoverArtState>>;
  editCoverArtMode: TEditCoverArtMode;
  setEditCoverArtMode: React.Dispatch<React.SetStateAction<TEditCoverArtMode>>;
  isEditingCoverArt: boolean;
  setIsEditingCoverArt: React.Dispatch<React.SetStateAction<boolean>>;
  showCoverArtArea: boolean;
  // hammer, kicker, subheadline
  hammerHidden: boolean;
  setHammerHidden: React.Dispatch<React.SetStateAction<boolean>>;
  toggleHammer: () => void;
  kickerHidden: boolean;
  setKickerHidden: React.Dispatch<React.SetStateAction<boolean>>;
  toggleKicker: () => void;
  subheadlineHidden: boolean;
  setSubheadlineHidden: React.Dispatch<React.SetStateAction<boolean>>;
  toggleSubheadline: () => void;
  mainContentLocation: string;
  setMainContentLocation: React.Dispatch<React.SetStateAction<string>>;
  metadataLocation: string;
  setMetadataLocation: React.Dispatch<React.SetStateAction<string>>;
  mediaGalleryCount: number; // Child images and videos not inline
  setMediaGalleryCount: React.Dispatch<React.SetStateAction<number>>;
  additionalContentCount: number; // Other child assets not inline
  setAdditionalContentCount: React.Dispatch<React.SetStateAction<number>>;
  statsMenuListWidgetIds: TStatsMenuList[];
  setStatsMenuListWidgetIds: React.Dispatch<React.SetStateAction<TStatsMenuList[]>>;
  imagePreviewState: PreviewUpdateState;
  setImagePreviewState: React.Dispatch<React.SetStateAction<PreviewUpdateState>>;
  // Relationships search modal state
  relationshipsSearchModalOpen: boolean;
  setRelationshipsSearchModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  relationshipsSearchModalConfig: RelationshipsSearchModalConfig | null;
  openRelationshipsSearchModal: (config?: RelationshipsSearchModalConfig) => void;
  closeRelationshipsSearchModal: () => void;
  // Save button split menu state
  splitSaveMenuState: SplitSaveMenuState;
  setSplitSaveMenuState: React.Dispatch<React.SetStateAction<SplitSaveMenuState>>;
  // Table asset grid states
  tableGridApiRef: React.MutableRefObject<GridApiPro | null>;
  tableState: TableCtxState;
  setTableState: React.Dispatch<React.SetStateAction<TableCtxState>>;
  // Dynamic collection modals states
  dcModalState: TDynamicModalState;
  setDcModalState: React.Dispatch<React.SetStateAction<TDynamicModalState>>;
  dcPromptModalState: TPromptModalState;
  setDcPromptModalState: React.Dispatch<React.SetStateAction<TPromptModalState>>;
};

// Configuration for the relationships search modal
export type RelationshipsSearchModalConfig = {
  typeFilter?: string[]; // Filter by asset type (image, video, etc.)
  replaceNodePosition?: number; // Position to replace in editor (removes placeholder)
  onUseAsset?: HandleUseAssetSelectFn; // Callback for "use asset" mode (e.g., FileFieldInput selection)
  relationshipType?: RelationshipType; // For relationship insertion mode
  footerActionMode?: 'default' | 'addOnly'; // Footer actions variant for modal-specific flows
  selectionMode?: 'single' | 'multiple'; // Restrict selection count in modal search lists
};

// -----------------------------------------------------------------
// Utility Functions
// -----------------------------------------------------------------

function locationToLeftDrawerState(location?: UseLocationType): LeftDrawerStates {
  if (location?.hash?.startsWith('#meta')) {
    return 'metadata';
  }
  if (location?.hash === '#stats') {
    return 'stats';
  }
  if (location?.hash === '#revisions') {
    return 'revisions';
  }
  return 'content';
}

// -----------------------------------------------------------------
// Context Provider and Hooks
// -----------------------------------------------------------------

export const AssetViewContext = createContext<AssetViewContextType | undefined>(
  undefined,
);

export const useAssetViewContext = () => {
  const context = useContext(AssetViewContext);
  if (!context) {
    throw new Error(
      'useAssetViewContext must be used within an AssetViewContextProvider',
    );
  }
  return context;
};

export const AssetViewContextProvider = memo(({ children }: AssetViewProviderProps) => {
  // Generic contexts and hooks
  const location = useLocation();

  // Syncstore and asset contexts
  const { recordCopy } = useAssetConfigContext();
  const { nxtSyncStore } = useAssetEditorsContext();
  const { updateFieldVisibility } = nxtSyncStore;

  // Drawer UI States
  const [leftDrawerContent, setLeftDrawerContent] = useState<
    AssetViewContextType['leftDrawerContent']
  >(() => locationToLeftDrawerState(location));

  const [leftDrawerOpen, setLeftDrawerOpen] = useLocalStorage({
    key: 'leftDrawerOpen',
    defaultValue: false,
  });

  const [rightDrawerContent, setRightDrawerContent] =
    useState<AssetViewContextType['rightDrawerContent']>('relationships');
  const [rightDrawerOpen, setRightDrawerOpen] = useState(false);

  // State for assistant
  const [assistantContent, setAssistantContent] = useState<string>('scoreCard');

  // Field visibility - use synced state when available, fallback to local state
  const [localHammerHidden, setLocalHammerHidden] = useState(true);
  const [localKickerHidden, setLocalKickerHidden] = useState(true);
  const [localSubheadlineHidden, setLocalSubheadlineHidden] = useState(true);

  // Externally triggered asset uploads (e.g., right-click "Set as" actions)
  const [previewUploadAsset, setPreviewUploadAsset] = useState<TriggerAssetDrop | null>(
    null,
  );
  const [coverUploadAsset, setCoverUploadAsset] = useState<TriggerAssetDrop | null>(null);

  /**
   * Cover art states
   * note: coverArtData is synced in CoverArtUpload.tsx
   * via syncstore updates for collaboration support
   */
  const [editCoverArtMode, setEditCoverArtMode] = useState<TEditCoverArtMode>(null);
  const [isEditingCoverArt, setIsEditingCoverArt] = useState(false);
  const [coverArtData, setCoverArtData] = useState<TCoverArtState>(initCoverArtData);

  // Cover art exists or input is open (editing/staging)
  const showCoverArtArea =
    Boolean(coverArtData?.fileSourceUrl) || editCoverArtMode === 'upload';

  // Get current visibility states from sync store or fallback to local
  // The local state is ONLY used as initial fallback, not as a synchronized value
  // This prevents loops where setters update sync store -> which would trigger
  // effects that update local state -> which could cause unwanted re-renders and re-execute setters.
  const hammerHidden =
    nxtSyncStore.state.syncStoreData?.fieldVisibility?.hammerHidden ?? localHammerHidden;
  const kickerHidden =
    nxtSyncStore.state.syncStoreData?.fieldVisibility?.kickerHidden ?? localKickerHidden;
  const subheadlineHidden =
    nxtSyncStore.state.syncStoreData?.fieldVisibility?.subheadlineHidden ??
    localSubheadlineHidden;

  // Field visibility - synced setter functions
  // These update the sync store and also update local state as a fallback for non-collab mode.
  // The reactive values above will pick up changes from sync store automatically.
  const setHammerHidden = useCallback(
    (hiddenOrUpdater: FieldVisibilitySetter) => {
      // Get current value from sync store for function updater
      const currentHidden =
        nxtSyncStore.state.syncStoreData?.fieldVisibility?.hammerHidden ?? true;
      const hidden =
        typeof hiddenOrUpdater === 'function'
          ? hiddenOrUpdater(currentHidden)
          : hiddenOrUpdater;

      // Update local state (used as fallback when sync store unavailable)
      setLocalHammerHidden(hidden);

      // Update sync store if sync store is available (has fieldVisibility initialized)
      if (nxtSyncStore.state.syncStoreData?.fieldVisibility !== undefined) {
        updateFieldVisibility({
          field: 'hammerHidden',
          hidden: hidden,
        });
      }
    },
    [nxtSyncStore, updateFieldVisibility],
  );

  const setKickerHidden = useCallback(
    (hiddenOrUpdater: FieldVisibilitySetter) => {
      // Get current value from sync store for function updater
      const currentHidden =
        nxtSyncStore.state.syncStoreData?.fieldVisibility?.kickerHidden ?? true;
      const hidden =
        typeof hiddenOrUpdater === 'function'
          ? hiddenOrUpdater(currentHidden)
          : hiddenOrUpdater;

      // Update local state (used as fallback when sync store unavailable)
      setLocalKickerHidden(hidden);

      // Update sync store
      // Only update if sync store is available (has fieldVisibility initialized)
      if (nxtSyncStore.state.syncStoreData?.fieldVisibility !== undefined) {
        updateFieldVisibility({
          field: 'kickerHidden',
          hidden: hidden,
        });
      }
    },
    [nxtSyncStore, updateFieldVisibility],
  );

  const setSubheadlineHidden = useCallback(
    (hiddenOrUpdater: FieldVisibilitySetter) => {
      // Get current value from sync store for function updater
      const currentHidden =
        nxtSyncStore.state.syncStoreData?.fieldVisibility?.subheadlineHidden ?? true;
      const hidden =
        typeof hiddenOrUpdater === 'function'
          ? hiddenOrUpdater(currentHidden)
          : hiddenOrUpdater;

      // Update local state (used as fallback when sync store unavailable)
      setLocalSubheadlineHidden(hidden);

      // Update sync store
      // Only update if sync store is available (has fieldVisibility initialized)
      if (nxtSyncStore.state.syncStoreData?.fieldVisibility !== undefined) {
        updateFieldVisibility({
          field: 'subheadlineHidden',
          hidden: hidden,
        });
      }
    },
    [nxtSyncStore, updateFieldVisibility],
  );

  // Toggle helpers
  const toggleHammer = useCallback(() => {
    setHammerHidden((prev) => !prev);
  }, [setHammerHidden]);

  const toggleKicker = useCallback(() => {
    setKickerHidden((prev) => !prev);
  }, [setKickerHidden]);

  const toggleSubheadline = useCallback(() => {
    setSubheadlineHidden((prev) => !prev);
  }, [setSubheadlineHidden]);

  const toggleLeftDrawer = useCallback(() => {
    setLeftDrawerOpen((prev) => !prev);
  }, [setLeftDrawerOpen]);

  // Media Gallery and Additional Content counts
  const [mediaGalleryCount, setMediaGalleryCount] = useState(0);
  const [additionalContentCount, setAdditionalContentCount] = useState(0);

  // Tracking between bundles and editors
  const [mainContentLocation, setMainContentLocation] = useState('title');
  const [metadataLocation, setMetadataLocation] = useState('sections');

  // Widget ids for the asset's stats dashboard for navigation.
  const [statsMenuListWidgetIds, setStatsMenuListWidgetIds] = useState<TStatsMenuList[]>(
    [],
  );

  /**
   * Image preview state for image asset file input
   * https://townnews.atlassian.net/browse/CMSADMIN-1260
   * has been moved up to a shared context share between
   * ImageMainContentBundle and Teaser components
   * as the Teaser component has situations where it
   * may 'inherit' the main image preview for display
   */
  const [imagePreviewState, setImagePreviewState] = useState<PreviewUpdateState>({
    // initial source to determine if input is populated
    // note: this may become a local blob upon new image upload
    fileSourceUrl: recordCopy?.files?.hires?.url || recordCopy?.files?.image?.url,
    // initial base preview - no crop/resize query params
    // used for the cropper source / fallback for render
    basePreviewUrl: getBaseCleanUrl(recordCopy?.files?.image?.url),
    // initial cropped/resized - includes crop/resize query params
    // upon cropped may become a local data url
    cropPreviewUrl: recordCopy?.files?.image?.url,
    displayFileNameString: recordCopy?.title,
    extension: recordCopy?.files?.image?.extension,
    view_crop: recordCopy?.files?.image?.view_crop,
    type: recordCopy?.files?.image?.type,
    size: recordCopy?.files?.image?.size,
    width: recordCopy?.files?.image?.width,
    height: recordCopy?.files?.image?.height,
  });

  // Relationships search modal state
  const [relationshipsSearchModalOpen, setRelationshipsSearchModalOpen] = useState(false);
  const [relationshipsSearchModalConfig, setRelationshipsSearchModalConfig] =
    useState<RelationshipsSearchModalConfig | null>(null);

  // Memoized modal handlers to prevent context re-renders
  const openRelationshipsSearchModal = useCallback(
    (config?: RelationshipsSearchModalConfig) => {
      setRelationshipsSearchModalConfig(config || null);
      setRelationshipsSearchModalOpen(true);
    },
    [],
  );

  const closeRelationshipsSearchModal = useCallback(() => {
    setRelationshipsSearchModalOpen(false);
    setRelationshipsSearchModalConfig(null);
  }, []);

  /**
   * ----------------------------------------------------------------
   * Table Asset grid states
   * ----------------------------------------------------------------
   * Some states lifted up for use in save process and other areas
   * that need access to the MUI DataGrid state and API refs.
   *
   * Placed here instead of AssetEditorsContext out of caution to
   * not distrub any stability of the editors context hook memo.
   * These can be moved to their own context provider in future.
   */
  const tableGridApiRef = useGridApiRef();
  const [tableState, setTableState] = useState<TableCtxState>({
    rows: null,
    columns: null,
    loading: true,
    loadingText: '',
  });

  /**
   * ----------------------------------------------------------------
   * Dynamic collection - modal states
   * ----------------------------------------------------------------
   * Handle dynamic or standard collection change
   * snapshot asset form values on modal open to state
   * show info prompt confirm before changing modes
   */
  const [dcModalState, setDcModalState] = useState<TDynamicModalState>(initDcModalState);

  const [dcPromptModalState, setDcPromptModalState] =
    useState<TPromptModalState>(initDcPromptModalState);

  /**
   * -----------------------------------------------------------------
   * Save Button Menu related states
   * -----------------------------------------------------------------
   */
  const [splitSaveMenuState, setSplitSaveMenuState] = useState(initSplitSaveMenuState);

  /**
   * Memoized context value
   */
  const contextValue = useMemo(() => {
    return {
      additionalContentCount,
      assistantContent,
      editCoverArtMode,
      hammerHidden,
      kickerHidden,
      leftDrawerContent,
      leftDrawerOpen,
      mainContentLocation,
      mediaGalleryCount,
      metadataLocation,
      rightDrawerContent,
      rightDrawerOpen,
      setAdditionalContentCount,
      setAssistantContent,
      setEditCoverArtMode,
      previewUploadAsset,
      setPreviewUploadAsset,
      coverUploadAsset,
      setCoverUploadAsset,
      coverArtData,
      showCoverArtArea,
      setCoverArtData,
      isEditingCoverArt,
      setIsEditingCoverArt,
      setHammerHidden,
      setKickerHidden,
      setSubheadlineHidden,
      setLeftDrawerOpen,
      setLeftDrawerContent,
      toggleLeftDrawer,
      toggleHammer,
      toggleKicker,
      toggleSubheadline,
      setMainContentLocation,
      setMediaGalleryCount,
      setMetadataLocation,
      setRightDrawerContent,
      setRightDrawerOpen,
      setStatsMenuListWidgetIds,
      statsMenuListWidgetIds,
      subheadlineHidden,
      imagePreviewState,
      setImagePreviewState,
      // Relationships search modal
      relationshipsSearchModalOpen,
      setRelationshipsSearchModalOpen,
      relationshipsSearchModalConfig,
      openRelationshipsSearchModal,
      closeRelationshipsSearchModal,
      // Save button split menu state
      splitSaveMenuState,
      setSplitSaveMenuState,
      // Table grid states
      tableGridApiRef,
      tableState,
      setTableState,
      // Dynamic collection modal state
      dcModalState,
      setDcModalState,
      dcPromptModalState,
      setDcPromptModalState,
    };
  }, [
    additionalContentCount,
    assistantContent,
    editCoverArtMode,
    hammerHidden,
    kickerHidden,
    leftDrawerContent,
    leftDrawerOpen,
    mainContentLocation,
    mediaGalleryCount,
    metadataLocation,
    rightDrawerContent,
    rightDrawerOpen,
    setAdditionalContentCount,
    setAssistantContent,
    setEditCoverArtMode,
    previewUploadAsset,
    setPreviewUploadAsset,
    coverUploadAsset,
    setCoverUploadAsset,
    coverArtData,
    showCoverArtArea,
    isEditingCoverArt,
    setIsEditingCoverArt,
    setCoverArtData,
    setHammerHidden,
    setKickerHidden,
    setLeftDrawerContent,
    setLeftDrawerOpen,
    setMainContentLocation,
    setMediaGalleryCount,
    setMetadataLocation,
    setRightDrawerContent,
    setRightDrawerOpen,
    setStatsMenuListWidgetIds,
    setSubheadlineHidden,
    statsMenuListWidgetIds,
    subheadlineHidden,
    imagePreviewState,
    setImagePreviewState,
    relationshipsSearchModalOpen,
    relationshipsSearchModalConfig,
    openRelationshipsSearchModal,
    closeRelationshipsSearchModal,
    splitSaveMenuState,
    setSplitSaveMenuState,
    toggleHammer,
    toggleKicker,
    toggleSubheadline,
    toggleLeftDrawer,
    tableGridApiRef,
    tableState,
    setTableState,
    dcModalState,
    setDcModalState,
    dcPromptModalState,
    setDcPromptModalState,
  ]);

  return (
    <AssetViewContext.Provider value={contextValue}>{children}</AssetViewContext.Provider>
  );
});
